import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  useTheme,
  Typography,
  IconButton,
  Divider,
  Chip
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import CategoryIcon from '@mui/icons-material/Category';

const CreateTaskModal = ({ open, handleClose, handleSave }) => {
  const theme = useTheme();
  const [taskData, setTaskData] = useState({
    title: "",
    description: "",
    priority: "Média",
    dueDate: new Date(),
    client: "",
    category: "",
    status: "Aguardando",
  });

  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTaskData({ ...taskData, [name]: value });
  };

  const handleDateChange = (date) => {
    setTaskData({ ...taskData, dueDate: date });
  };

  const saveTask = () => {
    handleSave(taskData);
    setTaskData({
      title: "",
      description: "",
      priority: "Média",
      dueDate: new Date(),
      client: "",
      category: "",
      status: "Aguardando",
    });
  };

  const getPriorityIcon = (level) => {
    const isSelected = taskData.priority === level;
    const colorMap = {
      "Alta": theme.palette.error.main,
      "Média": theme.palette.warning.main,
      "Baixa": theme.palette.success.main
    };

    return (
      <Box display="flex" alignItems="center">
        {isSelected ? (
          <StarIcon sx={{ color: colorMap[level], mr: 1 }} />
        ) : (
          <StarBorderIcon sx={{ color: theme.palette.text.disabled, mr: 1 }} />
        )}
        {level}
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[24],
          overflow: "hidden"
        }
      }}
    >
      {/* Premium Header */}
      <DialogTitle sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
        padding: "20px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <Typography variant="h6" sx={{ fontSize: "1.25rem", letterSpacing: "0.5px" }}>
          Criar Nova Tarefa
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: "inherit" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        <Box component="form" sx={{ 
          display: "flex", 
          flexDirection: "column", 
          gap: 3 
        }}>
          {/* Title Field */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: focusedField === 'title' ? theme.palette.primary.main : theme.palette.text.secondary,
              fontWeight: 600
            }}>
              Título da Tarefa *
            </Typography>
            <TextField
              name="title"
              value={taskData.title}
              onChange={handleChange}
              onFocus={() => setFocusedField('title')}
              onBlur={() => setFocusedField(null)}
              required
              variant="outlined"
              fullWidth
              placeholder="Digite o título da tarefa"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: focusedField === 'title' ? theme.palette.primary.main : theme.palette.divider,
                    borderWidth: "2px"
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.light
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <DescriptionIcon sx={{ 
                    color: focusedField === 'title' ? theme.palette.primary.main : theme.palette.text.disabled,
                    mr: 1.5
                  }} />
                )
              }}
            />
          </Box>

          {/* Description Field */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: focusedField === 'description' ? theme.palette.primary.main : theme.palette.text.secondary,
              fontWeight: 600
            }}>
              Descrição *
            </Typography>
            <TextField
              name="description"
              value={taskData.description}
              onChange={handleChange}
              onFocus={() => setFocusedField('description')}
              onBlur={() => setFocusedField(null)}
              multiline
              rows={4}
              required
              variant="outlined"
              fullWidth
              placeholder="Descreva os detalhes da tarefa..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: focusedField === 'description' ? theme.palette.primary.main : theme.palette.divider,
                    borderWidth: "2px"
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.light
                  }
                }
              }}
            />
          </Box>

          <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />

          {/* Client Field */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: focusedField === 'client' ? theme.palette.primary.main : theme.palette.text.secondary,
              fontWeight: 600
            }}>
              Cliente *
            </Typography>
            <TextField
              name="client"
              value={taskData.client}
              onChange={handleChange}
              onFocus={() => setFocusedField('client')}
              onBlur={() => setFocusedField(null)}
              required
              variant="outlined"
              fullWidth
              placeholder="Nome do cliente"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: focusedField === 'client' ? theme.palette.primary.main : theme.palette.divider,
                    borderWidth: "2px"
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.light
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <PersonIcon sx={{ 
                    color: focusedField === 'client' ? theme.palette.primary.main : theme.palette.text.disabled,
                    mr: 1.5
                  }} />
                )
              }}
            />
          </Box>

          {/* Priority Field */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: theme.palette.text.secondary,
              fontWeight: 600
            }}>
              Prioridade
            </Typography>
            <FormControl fullWidth>
              <Select
                name="priority"
                value={taskData.priority}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.divider,
                    borderWidth: "2px"
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.light
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: "10px",
                      marginTop: "8px",
                      boxShadow: theme.shadows[8]
                    }
                  }
                }}
              >
                <MenuItem value="Baixa">{getPriorityIcon("Baixa")}</MenuItem>
                <MenuItem value="Média">{getPriorityIcon("Média")}</MenuItem>
                <MenuItem value="Alta">{getPriorityIcon("Alta")}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Date Field */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: theme.palette.text.secondary,
              fontWeight: 600
            }}>
              Data de Vencimento
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
              <DatePicker
                value={taskData.dueDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        "& fieldset": {
                          borderColor: theme.palette.divider,
                          borderWidth: "2px"
                        },
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.light
                        }
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <EventIcon sx={{ 
                          color: theme.palette.text.disabled,
                          mr: 1.5
                        }} />
                      )
                    }}
                  />
                )}
                inputFormat="dd/MM/yyyy"
              />
            </LocalizationProvider>
          </Box>

          {/* Category Field */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: theme.palette.text.secondary,
              fontWeight: 600
            }}>
              Categoria
            </Typography>
            <FormControl fullWidth>
              <Select
                name="category"
                value={taskData.category}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.divider,
                    borderWidth: "2px"
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.light
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: "10px",
                      marginTop: "8px",
                      boxShadow: theme.shadows[8]
                    }
                  }
                }}
                startAdornment={
                  <CategoryIcon sx={{ 
                    color: theme.palette.text.disabled,
                    mr: 1.5
                  }} />
                }
              >
                <MenuItem value="Documentação">
                  <Chip label="Documentação" size="small" sx={{ mr: 1 }} />
                </MenuItem>
                <MenuItem value="Visto">
                  <Chip label="Visto" size="small" sx={{ mr: 1 }} color="info" />
                </MenuItem>
                <MenuItem value="Financeiro">
                  <Chip label="Financeiro" size="small" sx={{ mr: 1 }} color="success" />
                </MenuItem>
                <MenuItem value="Logística">
                  <Chip label="Logística" size="small" sx={{ mr: 1 }} color="warning" />
                </MenuItem>
                <MenuItem value="Outro">
                  <Chip label="Outro" size="small" sx={{ mr: 1 }} color="default" />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>

      {/* Premium Footer Actions */}
      <DialogActions sx={{ 
        padding: "16px 24px",
        borderTop: `1px solid ${theme.palette.divider}`,
        justifyContent: "space-between"
      }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            borderRadius: "10px",
            padding: "10px 20px",
            borderColor: theme.palette.divider,
            "&:hover": {
              borderColor: theme.palette.primary.light,
              color: theme.palette.primary.main
            },
            minWidth: "120px"
          }}
        >
          Cancelar
        </Button>
        <Button 
          onClick={saveTask} 
          variant="contained"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            "&:hover": {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.darker} 100%)`,
              boxShadow: theme.shadows[4]
            },
            fontWeight: 700,
            borderRadius: "10px",
            padding: "10px 24px",
            boxShadow: theme.shadows[2],
            minWidth: "160px",
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}
          disabled={!taskData.title || !taskData.description || !taskData.client}
        >
          Criar Tarefa
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskModal;